/**
 * Utility functions for time formatting and calculations
 */
import { toWireTime } from '@bower/shared/utils/wire-time';

/**
 * Format a timestamp for the hover tooltip next to a relative "X hours ago"
 * label: two lines, the viewer's local time with its zone named (abbreviation
 * and IANA name), then the same instant in UTC.
 *
 *   Fri, 17 Oct 2026, 10:00:00 AEST (Australia/Brisbane)
 *   2026-10-17 00:00:00 UTC
 *
 * The UTC line is what the server stored, so two people in different zones can
 * agree on the exact moment (audit evidence in particular). Render it where a
 * newline shows: a native `title`, or a Chakra `Tooltip` with
 * `whiteSpace="pre-line"`. An unreadable timestamp gives an empty string.
 */
export function formatTimestampTooltip(timestamp: string | Date): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const local = date.toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const utc = toWireTime(date).slice(0, 19).replace('T', ' ');
  // The IANA name adds nothing where the abbreviation already is it ("UTC (UTC)").
  const named = zone && !local.endsWith(zone) ? ` (${zone})` : '';
  return `${local}${named}\n${utc} UTC`;
}

/**
 * Formats a timestamp as relative time (e.g., "Just now", "5 minutes ago", "2 hours ago")
 * @param timestamp - Date string or Date object
 * @returns Formatted relative time string
 */
export function formatTimeAgo(timestamp: string | Date): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 2) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
  return date.toLocaleDateString();
}

/**
 * A camera's zone-less wall clock ("2026-10-20T02:29:28", EXIF
 * DateTimeOriginal with no OffsetTimeOriginal) as it was written: "Oct 20,
 * 2026, 02:29". Never shifted into the viewer's zone and never "ago": the zone
 * it was written in is unknown, so it is not an instant (`naiveWallClockSchema`,
 * `rules/time.md`). Undefined for anything that is not such a wall clock.
 */
export function formatCameraClock(wallClock: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(wallClock);
  if (!match) return undefined;
  const [, year, month, day, hour, minute] = match.map(Number);
  // Built and formatted in UTC so the digits come back exactly as written.
  const asWritten = new Date(Date.UTC(year, month - 1, day, hour, minute));
  if (Number.isNaN(asWritten.getTime())) return undefined;
  return formatDate(asWritten, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
  });
}

/**
 * Formats a timestamp as a short relative time for compact displays
 * @param timestamp - Date string or Date object
 * @returns Short formatted time string (e.g., "2m", "1h", "3d")
 */
export function formatTimeAgoShort(timestamp: string | Date): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 2) {
    return 'now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m`;
  } else if (diffHours < 24) {
    return `${diffHours}h`;
  } else if (diffDays < 7) {
    return `${diffDays}d`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Formats a date for display in tables or UI elements
 * @param timestamp - Date string or Date object
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(timestamp: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const date = new Date(timestamp);
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };

  return date.toLocaleDateString('en-US', options || defaultOptions);
}

/**
 * A list time with its day, local, on a 24-hour clock: "Today, 09:12",
 * "Oct 15, 14:02", or "Oct 15, 2025, 14:02" for an earlier year. Built per
 * call, so it follows the viewer's zone.
 */
export function formatDayAndTime(timestamp: string | Date, now: Date = new Date()): string {
  const date = new Date(timestamp);
  const clock: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  };
  if (date.toDateString() === now.toDateString()) {
    return `Today, ${date.toLocaleTimeString('en-US', clock)}`;
  }
  return formatDate(date, {
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    month: 'short',
    day: 'numeric',
    ...clock,
  });
}

/**
 * Formats duration in seconds to a human-readable string
 * @param durationInSeconds - Duration in seconds (number or string)
 * @returns Formatted duration string (e.g., "3:45", "23s")
 */
export function formatDuration(durationInSeconds: number | string): string {
  const seconds =
    typeof durationInSeconds === 'string' ? parseFloat(durationInSeconds) : durationInSeconds;
  if (isNaN(seconds) || seconds <= 0) return '';

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  if (minutes > 0) {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${remainingSeconds}s`;
}

/**
 * Generates a formatted capture title with a prefix, date, and time.
 * Format: "{prefix} DD-MMM-YYYY HH:MM" (e.g., "Voice Note 23-SEP-2025 23:12")
 * @param prefix - Label prefix (e.g., "Voice Note", "Photo")
 * @param date - Optional date to use, defaults to current time
 */
export function generateCaptureTitle(prefix: string, date?: Date): string {
  const now = date || new Date();

  const day = now.getDate().toString().padStart(2, '0');
  const monthNames = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
  ];
  const month = monthNames[now.getMonth()];
  const year = now.getFullYear();

  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');

  return `${prefix} ${day}-${month}-${year} ${hours}:${minutes}`;
}

/**
 * Generates a formatted voice note title.
 * Format: "Voice Note DD-MMM-YYYY HH:MM" (e.g., "Voice Note 23-SEP-2025 23:12")
 */
export function generateVoiceNoteTitle(date?: Date): string {
  return generateCaptureTitle('Voice Note', date);
}

/**
 * Parses an ISO DateTime string to a Date object, extracting just the date part
 * This prevents timezone offset issues when loading calendar dates
 * @param dateString - ISO DateTime string (YYYY-MM-DDT00:00:00.000Z or YYYY-MM-DD)
 * @returns Date object set to midnight in local timezone
 * @example
 * // Backend returns "2025-01-15T00:00:00.000Z"
 * parseDateOnly("2025-01-15T00:00:00.000Z") // Returns Date object for Jan 15, 2025 at midnight local time
 * // NOT Jan 14 or Jan 16 due to timezone conversion
 */
export function parseDateOnly(dateString: string): Date {
  // Extract just the date part (YYYY-MM-DD) from ISO string
  const datePart = dateString.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Coerce a value from a `@mantine/dates` picker to a Date at LOCAL midnight.
 *
 * Mantine 8 emits picked days as 'YYYY-MM-DD' strings, not Dates. Never hand
 * one to `new Date(str)`: a date-only string parses as UTC midnight, which
 * moves the day by the viewer's UTC offset (in Sydney "1 Oct" became 10:00
 * local, in Los Angeles it became 30 Sep). Every picker value goes through
 * here, so a picked day means the same local calendar day everywhere.
 */
export function parsePickerDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (typeof value === 'string' && value) return parseDateOnly(value);
  return null;
}

/**
 * The viewer's own time zone as the browser names it at `at` ("AEDT",
 * "GMT+11"; DST-aware, so pass the moment being shown). Every displayed time
 * in the app is in this zone; this names it. Empty when the browser offers no
 * name.
 */
export function localZoneName(at: Date): string {
  return (
    new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName')?.value ?? ''
  );
}

/**
 * An exact timestamp in the viewer's local time, with the zone named:
 * "2026-10-01 09:30:00 GMT+10". For audit-style rows that need second
 * precision; matches the local day headers they sit under. Never
 * `toISOString()` for display, which is UTC and carries no label.
 */
export function formatLocalTimestamp(timestamp: string | Date): string {
  const d = new Date(timestamp);
  const pad = (n: number) => String(n).padStart(2, '0');
  const zone = localZoneName(d);
  const day = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  return `${day} ${time} ${zone}`.trimEnd();
}
